import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Loader2, Film, Layers, Lock, Pencil, GripVertical, RefreshCw,
  CalendarSearch, Sparkles, CheckCircle2,
} from "lucide-react";

const TAG_CLS = "text-[10px] tracking-[0.3em] uppercase text-[#A69076]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso.slice(0, 16);
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" })
    + " · " + d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const toLocalInput = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtGapDate = (ymd) => {
  const d = new Date(ymd + "T12:00:00");
  if (isNaN(d)) return ymd;
  return d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
};

const SERVICE_LABEL = { ortodoncia: "Ortodoncia", estetica: "Estética", general: "General" };

export default function FeedPreviewPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [gapsOpen, setGapsOpen] = useState(false);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gaps, setGaps] = useState([]);
  const [fillingDate, setFillingDate] = useState(null);
  const [filledDates, setFilledDates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/feed/preview");
      setItems(r.data?.items || []);
    } catch { toast.error("Error cargando el feed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upcoming = useMemo(() => items.filter((i) => !i.published && !i.unscheduled), [items]);
  const unscheduled = useMemo(() => items.filter((i) => i.unscheduled), [items]);
  const published = useMemo(() => items.filter((i) => i.published && !i.unscheduled), [items]);

  const onDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...upcoming];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx, 0, moved);
    setDragIdx(null); setOverIdx(null);
    setSaving(true);
    // Optimistic: show new order immediately (dates get reassigned server-side)
    setItems([...arr, ...published]);
    try {
      const r = await api.post("/feed/reorder", {
        order: arr.map((i) => ({ id: i.id, kind: i.kind })),
      });
      toast.success(`Feed reordenado · ${r.data?.changed ?? 0} fechas reasignadas`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reordenando");
      await load();
    } finally { setSaving(false); }
  };

  const detectGaps = async () => {
    setGapsOpen(true);
    setGapsLoading(true);
    setGaps([]); setFilledDates([]);
    try {
      const r = await api.get("/feed/gaps");
      setGaps(r.data?.gaps || []);
      if ((r.data?.gaps || []).length === 0) toast.success("¡Sin huecos! Todos los días tienen contenido 🎉");
    } catch { toast.error("Error detectando huecos"); }
    finally { setGapsLoading(false); }
  };

  const fillGap = async (g) => {
    setFillingDate(g.date);
    try {
      await api.post("/feed/fill-gap", {
        date: g.date, topic: g.topic, service: g.service, hora: g.hora || "18:00",
      });
      setFilledDates((prev) => [...prev, g.date]);
      toast.success(`Post en cola para el ${fmtGapDate(g.date)} — se genera en ~1 min`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando el post");
    } finally { setFillingDate(null); }
  };

  const saveDate = async () => {
    if (!editItem || !editDate) return;
    setSaving(true);
    try {
      const iso = new Date(editDate).toISOString();
      await api.patch(`/feed/${editItem.kind}/${editItem.id}/schedule`, { scheduled_for: iso });
      toast.success(`Reprogramado al ${fmtDate(iso)}`);
      setEditItem(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reprogramando");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="feed-preview-panel">
      {/* IG profile mockup header */}
      <div className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden">
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-full border-2 border-[#D97757] p-0.5 flex-shrink-0">
            <div className="w-full h-full rounded-full bg-[#1A1C1A] flex items-center justify-center">
              <span className="font-display text-2xl text-[#A69076]">tm</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-display text-xl text-[#1A1C1A]">dra.taniamendoza</div>
            <div className="flex gap-5 mt-1.5 text-sm text-[#1A1C1A]">
              <span><strong>{items.length}</strong> <span className="text-[#5C5E5C]">posts</span></span>
              <span><strong>{upcoming.length}</strong> <span className="text-[#5C5E5C]">programados</span></span>
              <span><strong>{published.length}</strong> <span className="text-[#5C5E5C]">publicados</span></span>
            </div>
            <div className="text-xs text-[#5C5E5C] mt-1.5 leading-snug">
              Ortodoncia & Medicina Estética · Pachuca, Hgo.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm" onClick={detectGaps}
              className="bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs gap-1.5 h-9"
              data-testid="feed-detect-gaps-btn"
            >
              <CalendarSearch className="w-3.5 h-3.5" /> Detectar huecos
            </Button>
            <Button
              variant="ghost" size="sm" onClick={load}
              className="text-[#A69076] hover:text-[#1A1C1A]"
              data-testid="feed-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <div className="px-6 pb-4 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C]">
          <GripVertical className="w-3.5 h-3.5 text-[#D97757]" />
          Arrastra un post sobre otro para intercambiar su lugar en el feed — las fechas se reasignan solas.
          Click en el lápiz para cambiar día y hora exactos.
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#A69076] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#A69076] italic">
            No hay posts programados todavía.
          </div>
        ) : (
          <>
            {/* Unscheduled launch items (warning strip) */}
            {unscheduled.length > 0 && (
              <>
                <div className="px-6 py-2.5 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#965D5D] border-t border-[#E6E2DC] bg-[#FBF3F0]">
                  ⚠ Sin fecha asignada ({unscheduled.length}) — click en el lápiz para programarlos
                </div>
                <div className="grid grid-cols-3 gap-0.5 bg-[#E6E2DC]" data-testid="feed-grid-unscheduled">
                  {unscheduled.map((it) => (
                    <FeedTile
                      key={`${it.kind}-${it.id}`}
                      item={it}
                      onEdit={() => { setEditItem(it); setEditDate(""); }}
                    />
                  ))}
                </div>
              </>
            )}
            {/* Upcoming (draggable) */}
            <div className="grid grid-cols-3 gap-0.5 bg-[#E6E2DC]" data-testid="feed-grid-upcoming">
              {upcoming.map((it, i) => (
                <FeedTile
                  key={`${it.kind}-${it.id}`}
                  item={it}
                  draggable
                  dragging={dragIdx === i}
                  dragOver={overIdx === i && dragIdx !== null && dragIdx !== i}
                  onDragStart={() => setDragIdx(i)}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                  onDrop={(e) => { e.preventDefault(); onDrop(i); }}
                  onEdit={() => { setEditItem(it); setEditDate(toLocalInput(it.scheduled_for)); }}
                />
              ))}
            </div>
            {/* Published (locked) */}
            {published.length > 0 && (
              <>
                <div className="px-6 py-2.5 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#A69076] border-t border-[#E6E2DC]">
                  <Lock className="w-3 h-3" /> Ya publicados
                </div>
                <div className="grid grid-cols-3 gap-0.5 bg-[#E6E2DC]" data-testid="feed-grid-published">
                  {published.map((it) => (
                    <FeedTile key={`${it.kind}-${it.id}`} item={it} locked />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {saving && (
        <div className="fixed bottom-6 right-6 bg-[#1A1C1A] text-[#F4F0EB] text-xs px-4 py-2.5 rounded-full flex items-center gap-2 shadow-lg z-50">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…
        </div>
      )}

      {/* Gaps detection dialog */}
      <Dialog open={gapsOpen} onOpenChange={(v) => { setGapsOpen(v); if (!v && filledDates.length) load(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="feed-gaps-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Huecos en el calendario</DialogTitle>
          </DialogHeader>
          {gapsLoading ? (
            <div className="py-10 text-center space-y-2">
              <Loader2 className="w-6 h-6 animate-spin text-[#D97757] mx-auto" />
              <p className="text-xs text-[#5C5E5C]">Analizando el calendario y sugiriendo temas con IA…</p>
            </div>
          ) : gaps.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#A69076]">
              🎉 Todos los días tienen contenido programado.
            </div>
          ) : (
            <div className="space-y-2.5">
              <p className="text-xs text-[#5C5E5C]">
                {gaps.length} día{gaps.length === 1 ? "" : "s"} sin contenido. Cada sugerencia genera
                un post completo (texto + imagen con tu estilo) programado a las 18:00.
              </p>
              {gaps.map((g) => {
                const filled = filledDates.includes(g.date);
                return (
                  <div key={g.date} className="border border-[#E6E2DC] rounded-lg p-3 flex items-start gap-3" data-testid={`feed-gap-${g.date}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#D97757] font-medium">
                        {fmtGapDate(g.date)}
                      </div>
                      <p className="text-sm text-[#1A1C1A] mt-1 leading-snug">{g.topic || "(sin sugerencia — escribe tu tema en Posts sueltos & IA)"}</p>
                      <span className="inline-block mt-1.5 text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#F4F0EB] text-[#A69076]">
                        {SERVICE_LABEL[g.service] || g.service}
                      </span>
                    </div>
                    {filled ? (
                      <span className="flex items-center gap-1 text-[10px] text-[#5a7d5a] font-medium flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-4 h-4" /> En cola
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => fillGap(g)}
                        disabled={fillingDate !== null || !g.topic}
                        className="bg-[#D97757] hover:bg-[#C66749] text-white text-xs gap-1 flex-shrink-0"
                        data-testid={`feed-gap-fill-${g.date}`}
                      >
                        {fillingDate === g.date
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <><Sparkles className="w-3 h-3" /> Crear con IA</>}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit date/time dialog */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="max-w-sm" data-testid="feed-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Cambiar día y hora</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {editItem.image_url ? (
                  <img src={`${BACKEND}${editItem.image_url}`} alt="" className="w-16 h-16 rounded object-cover bg-[#F4F0EB]" />
                ) : (
                  <div className="w-16 h-16 rounded bg-[#F4F0EB] flex items-center justify-center"><Film className="w-5 h-5 text-[#A69076]" /></div>
                )}
                <div className="min-w-0 text-xs">
                  <div className="text-[#1A1C1A] line-clamp-2">{editItem.topic}</div>
                  <div className="text-[#A69076] mt-1">{editItem.kind === "reel" ? "Reel" : editItem.is_carousel ? "Carrusel" : "Post"} · actual: {fmtDate(editItem.scheduled_for)}</div>
                </div>
              </div>
              <div>
                <Label className={TAG_CLS + " mb-1.5 block"}>Nueva fecha y hora</Label>
                <Input
                  type="datetime-local"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  data-testid="feed-edit-datetime"
                />
              </div>
              <Button
                onClick={saveDate}
                disabled={saving || !editDate}
                className="w-full bg-[#D97757] hover:bg-[#C66749] text-white"
                data-testid="feed-edit-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeedTile({ item, locked = false, draggable = false, dragging = false, dragOver = false,
                     onDragStart, onDragEnd, onDragOver, onDrop, onEdit }) {
  const src = item.image_url ? `${BACKEND}${item.image_url}` : null;
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`relative aspect-square bg-[#F4F0EB] group select-none overflow-hidden
        ${draggable ? "cursor-grab active:cursor-grabbing" : ""}
        ${dragging ? "opacity-40" : ""}
        ${dragOver ? "ring-4 ring-inset ring-[#D97757]" : ""}`}
      data-testid={`feed-tile-${item.id}`}
    >
      {src ? (
        <img src={src} alt="" draggable={false} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Film className="w-7 h-7 text-[#A69076]" />
        </div>
      )}
      {/* Type icon (top-right, like IG) */}
      {item.kind === "reel" && (
        <Film className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow" />
      )}
      {item.is_carousel && (
        <Layers className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow" />
      )}
      {locked && <Lock className="absolute top-2 left-2 w-3.5 h-3.5 text-white/90 drop-shadow" />}
      {/* Date badge */}
      <div className={`absolute bottom-0 inset-x-0 px-2 py-1.5 text-[10px] text-white flex items-center justify-between gap-1
        bg-gradient-to-t ${locked ? "from-black/60" : "from-[#1A1C1A]/85"} to-transparent`}>
        <span className={`truncate font-medium ${item.unscheduled ? "text-[#F0B4A0]" : ""}`}>
          {item.unscheduled
            ? `Sin fecha${item.launch_day ? ` · Día ${item.launch_day}` : ""}`
            : fmtDate(item.scheduled_for)}
        </span>
        {!locked && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            className="p-1 rounded bg-white/15 hover:bg-[#D97757] transition opacity-0 group-hover:opacity-100 flex-shrink-0"
            title="Cambiar día y hora"
            data-testid={`feed-tile-edit-${item.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
